/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, Fragment } from 'react'
import { Form, Input, Button, message } from 'antd'
import { RedoOutlined, LoadingOutlined } from '@ant-design/icons'
import { getCode, verifyCode, getTotpBindUrl, verifyTotpAuthCode, saveUserMfaConfig } from '@/services/mfa'
import QRCode from 'qrcode.react'
import VerifyCode from '../../../VerifyCode'
import intl from 'react-intl-universal'
import { PATTERN, INPUT_TIP, MFA_TYPE } from '@/config/default'

function MFABindForm ({ type, onSuccess }) {
  const [form] = Form.useForm()
  const [qrUrl, setQrUrl] = useState('')
  const [QRLoading, setQRLoading] = useState(true)
  const [qrCount, setQrCount] = useState(0)
  const [accountValue, setAccountValue] = useState('')
  const intervalRef = useRef(null)

  /** 获取TOTP绑定URL，并生成二维码 */
  const getTotpBindUrlAndQrCode = () => {
    setQRLoading(true)
    ;(async () => {
      try {
        const result = await getTotpBindUrl()
        setQrUrl(result)
        countDownQrCode(300)
      } catch (error) {
        console.log('获取TOTP绑定URL失败：', error)
      } finally {
        setQRLoading(false)
      }
    })()
  }

  /** 二维码有效期倒计时5分钟 */
  const countDownQrCode = (time) => {
    let count = time
    intervalRef.current = setInterval(() => {
      count -= 1
      setQrCount(count)
      if (count === 0) {
        clearInterval(intervalRef.current)
        setQrUrl('')
      }
    }, 1000)
  }

  /** 刷新二维码 */
  const refreshQRCode = () => {
    getTotpBindUrlAndQrCode()
  }

  /** 绑定手机或邮箱 */
  const bindings = async (e) => {
    e.preventDefault()
    try {
      const deviceFingerprint = localStorage.getItem('react_device_fingerprint')
      const values = await form.validateFields()
      await verifyCode({ ...values, type: type, deviceFingerprint })
      await saveUserMfaConfig({ ...values, type: type })
      message.success(intl.get('mfa.name_bindingSuccessful', { name: MFA_TYPE[type] }))
      onSuccess()
    } catch (error) {
      console.log('绑定手机或邮箱失败：', error)
    }
  }

  /** 绑定TOTP */
  const totpBindings = async (e) => {
    e.preventDefault()
    try {
      const deviceFingerprint = localStorage.getItem('react_device_fingerprint')
      const values = await form.validateFields()
      await verifyTotpAuthCode({ ...values, deviceFingerprint })
      await saveUserMfaConfig({ ...values, type: type })
      message.success(intl.get('mfa.name_bindingSuccessful', { name: MFA_TYPE.TOTP }))
      onSuccess()
    } catch (error) {
      console.log('绑定TOTP失败：', error)
    }
  }

  /** 复制TOTP秘钥 */
  const copyTotpSecret = async () => {
    // //totp/yunding:guoxiu?secret=ET5NQN4IOXR7D45D6AZIC37YYBY5MHYO&issuer=yunding&algorithm=SHA1&digits=6&period=30
    const secret = qrUrl ? qrUrl.split('secret=')[1].split('&')[0] : ''
    if (secret) {
      try {
        await navigator.clipboard.writeText(secret)
        message.success(intl.get('mfa.copySuccessful'))
      } catch (err) {
        console.error('无法复制文本: ', err)
        message.error(intl.get('mfa.copyFailed'))
      }
    } else {
      message.error(intl.get('mfa.theSecretKeyDoesNotExist'))
    }
  };

  const handleAccount = (e) => {
    setAccountValue(e.target.value)
  }

  useEffect(() => {
    if (type === 'TOTP') {
      getTotpBindUrlAndQrCode()
      clearInterval(intervalRef.current)
    }
    return () => {
      clearInterval(intervalRef.current)
    }
  }, [])

  useEffect(() => {
    if (type === 'TOTP') {
      getTotpBindUrlAndQrCode()
      setQrCount(0)
      clearInterval(intervalRef.current)
    }
  }, [type])

  return (
    <Fragment>
      {
        (type === 'MOBILE' || type === 'EMAIL') && (
          <div className='bind-mfa-form'>
            <Form layout='vertical' form={form}>
              <Form.Item
                name={type.toLocaleLowerCase()}
                rules={[
                  { required: true, message: INPUT_TIP[type + '_EMPTY'] },
                  { pattern: PATTERN[type], message: INPUT_TIP.FORMAT_ERROR }
                ]}
              >
                <Input size='large' type='text' placeholder={INPUT_TIP[type]} allowClear onChange={handleAccount} />
              </Form.Item>
              <Form.Item
                name="code"
                rules={[
                  { required: true, message: INPUT_TIP.CODE_EMPTY },
                  { pattern: PATTERN.CODE, message: INPUT_TIP.CODE_ERROR }
                ]}
              >
                <VerifyCode form={form} placeholder={INPUT_TIP.CODE} type={type} accountValue={accountValue} />
              </Form.Item>
              <Form.Item>
                <Button type='primary' onClick={bindings} >{intl.get('mfa.confirm')}</Button>
              </Form.Item>
            </Form>
          </div>
        )
      }
      {
        type === 'TOTP' && (
          <div className='bind-mfa-form bind-totp-form'>
            <div className='bind-totp-container'>
              <QRCode
                value={qrUrl}
                size={256}
                level="M" // 容错级别：L(7%) < M(15%) < Q(25%) < H(30%)
                includeMargin={true} // 是否包含边距
                {...qrUrl
                  ? {
                    bgColor: '#FFFFFF',
                    fgColor: '#000000'
                  }
                  : {
                    fgColor: '#cccccc'
                  }} />
              {!qrUrl && <div className='refresh-QRCode'>
                {!QRLoading && <RedoOutlined onClick={refreshQRCode} />}
                {QRLoading && <LoadingOutlined />}
              </div>}
            </div>
            <Button type='link' onClick={copyTotpSecret}>{intl.get('mfa.copySecretKey')}</Button>
            <Form layout='vertical' form={form}>
              <Form.Item
                name="code"
                rules={[
                  { required: true, message: INPUT_TIP.TOTP_EMPTY },
                  { pattern: PATTERN.CODE, message: INPUT_TIP.TOTP }
                ]}
              >
                <Input placeholder={INPUT_TIP.TOTP} allowClear size="large" />
              </Form.Item>
              <Form.Item>
                <Button type='primary' onClick={totpBindings}>{intl.get('mfa.confirm')}</Button>
              </Form.Item>
            </Form>
          </div>
        )
      }
    </Fragment>
  )
}

export default MFABindForm